import React, { useRef, useState } from 'react'

import { useAssociatedDeviceActions } from '../hooks/useAssociatedDeviceActions'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'

import {
  Box,
  HStack,
  Skeleton,
  VStack,
  useOutsideClick,
} from '@chakra-ui/react'

import {
  AddIcon,
  DarkProvider,
  Modal,
  TypeaheadSearch,
  UtilityButton,
} from '@hivewatch/dark'

import { useAssociatedDevicesQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociatedDeviceRow } from '../types/types'
import { deriveRows } from '../utils/utils'
import { AssociatedDevice } from './AssociatedDevice'

// ─── Sub-components ───────────────────────────────────────────────────────────

const RowSkeleton = () => {
  return (
    <HStack justifyContent='space-between' px='24px' py='12px'>
      <HStack spacing='2'>
        <Skeleton h='16px' rounded='4px' w='16px' />
        <Skeleton h='16px' rounded='4px' w='140px' />
      </HStack>
      <Skeleton h='26px' rounded='full' w='110px' />
    </HStack>
  )
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface AssociatedDevicesTabIProps {
  deviceId: string
  deviceFloorId?: string | null
  deviceName?: string | null
  deviceType?: DeviceType
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AssociatedDevicesTab = ({
  deviceId,
  deviceFloorId,
  deviceName,
  deviceType = DeviceType.Camera,
}: AssociatedDevicesTabIProps) => {
  const mode = useLightMode()

  const { data, loading: isLoading } = useAssociatedDevicesQuery({
    variables: { id: deviceId },
    nextFetchPolicy: 'cache-and-network',
  })

  const { shouldEnableAssociateDevices } = usePermissions()

  const { availableOptions, addDevice, removeDevice, isAdding, removingId } =
    useAssociatedDeviceActions({
      deviceId,
      deviceType,
      deviceFloorId,
      associatedDevicesData: data,
      enabled: shouldEnableAssociateDevices,
    })

  const [isAddingOpen, setIsAddingOpen] = useState(false)
  const [pendingRemoveRow, setPendingRemoveRow] =
    useState<AssociatedDeviceRow | null>(null)
  const addContainerRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: addContainerRef as React.RefObject<HTMLElement>,
    handler: () => setIsAddingOpen(false),
  })

  const rows = deriveRows(data, deviceId)

  if (isLoading && !data) {
    return (
      <VStack align='stretch' spacing='0'>
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </VStack>
    )
  }

  return (
    <DarkProvider>
      <VStack align='stretch' spacing='0'>
        {rows.length === 0 && (
          <Box color='gray.500' fontSize='14px' px='24px' py='16px'>
            No associated devices.
          </Box>
        )}
        {rows.map((row) => (
          <AssociatedDevice
            isRemoving={removingId === row.id}
            key={row.id}
            mainDeviceId={deviceId}
            mainDeviceName={deviceName ?? ''}
            mainDeviceType={deviceType}
            onRemove={(row) => setPendingRemoveRow(row)}
            row={row}
            shouldEnableAssociateDevices={shouldEnableAssociateDevices}
          />
        ))}
      </VStack>
      {shouldEnableAssociateDevices && (
        <Box px='24px' py='12px' ref={addContainerRef}>
          {isAddingOpen ? (
            <TypeaheadSearch
              colorScheme={mode}
              maxDropdownHeight={180}
              maxResults={100}
              onDismiss={() => setIsAddingOpen(false)}
              onSelect={(value) => {
                addDevice(value)
                setIsAddingOpen(false)
              }}
              options={availableOptions}
              placeholder='Create a new device association'
              showResultsWhenEmpty
            />
          ) : (
            <UtilityButton
              colorScheme={mode}
              disabled={isAdding}
              icon={<AddIcon size={16} />}
              label='Add device association'
              onClick={() => setIsAddingOpen(true)}
              tooltipLabel='Add device association'
            />
          )}
        </Box>
      )}
      {pendingRemoveRow && (
        <Modal
          colorScheme={mode}
          isOpen={!!pendingRemoveRow}
          maxW='480px'
          onCancel={() => setPendingRemoveRow(null)}
          onClose={() => setPendingRemoveRow(null)}
          onSave={() => {
            removeDevice(pendingRemoveRow)
            setPendingRemoveRow(null)
          }}
          saveButtonLabel='Yes'
          showCancelButton
          size='compact'
          title='Are you sure?'
          zIndex={1500}
        >
          Removing this association will also clear any Grids Drawn and
          Tailgating Enabled status for this device. You&apos;ll need to set
          these up again if you want to restore them.
        </Modal>
      )}
    </DarkProvider>
  )
}
